import React, { memo, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';
import { trim } from 'ramda';
import { Box, useTheme, Chip, darken, lighten } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { BaseButton, BaseField, LinkButton } from '@alycecom/ui';

import useResponsive from '../../../../../hooks/useResponsive';
import { maxLengthValidator } from '../../../../../helpers/validation.helpers';
import { IAlyceCustomTheme } from '../../../../../styles/alyceTheme';

const useStyles = makeStyles<IAlyceCustomTheme>(({ spacing, palette, zIndex, breakpoints }) => ({
  noteLink: {
    width: 130,
    marginRight: spacing(2),
  },
  mobileView: {
    position: 'fixed',
    bottom: 0,
    backgroundColor: palette.common.white,
    boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.1)',
    zIndex: zIndex.appBar,
  },
  submitButton: {
    [breakpoints.down('sm')]: {
      fontSize: 14,
      fontWeight: 'bold',
    },
  },
  deleteButton: {
    lineHeight: 1,
    fontWeight: 'bold',
    backgroundColor: palette.error.main,
    color: palette.common.white,
  },
  chipDeleteIcon: {
    color: palette.customStyling.secondaryCtaColor,
    width: 16,
    height: 16,
    '&:hover': {
      color: palette.customStyling.secondaryCtaColor,
    },
  },
}));

export interface IMeetingNotesFormProps {
  notes?: string;
  hasIntegration?: boolean;
  isLoading?: boolean;
  selectedSlotsCount?: number;
  onSubmit: (notes: string) => void;
  onClear: () => void;
}

const MeetingNotesForm = React.forwardRef(
  (
    {
      notes = '',
      hasIntegration = false,
      isLoading = false,
      selectedSlotsCount = 0,
      onSubmit,
      onClear,
    }: IMeetingNotesFormProps,
    ref,
  ) => {
    const classes = useStyles();
    const {
      palette: { customStyling },
    } = useTheme<IAlyceCustomTheme>();
    const { isMobile } = useResponsive();

    const [localNotes, setLocalNotes] = useState(notes);
    const [localErrors, setLocalErrors] = useState({});
    const [isNotesFieldVisible, setIsNotesFieldVisible] = useState(true);

    const isClearButtonVisible = isMobile && selectedSlotsCount > 0;
    const disabled = selectedSlotsCount === 0;

    let buttonText = 'Continue';
    if (selectedSlotsCount === 1) {
      buttonText = `Submit selected time`;
    }
    if (!hasIntegration && selectedSlotsCount > 1) {
      buttonText = `Submit (${selectedSlotsCount}) available times`;
    }

    const handleChangeNotes = useCallback(({ target: { value } }) => setLocalNotes(value), []);
    const handleSubmit = useCallback(
      e => {
        const error = localNotes ? maxLengthValidator(255)(trim(localNotes)) : undefined;
        if (error) {
          e.preventDefault();
          e.stopPropagation();
          setLocalErrors({ notes: [error] });
          return;
        }
        onSubmit(localNotes);
      },
      [onSubmit, localNotes],
    );
    const handleResetError = useCallback(() => setLocalErrors({}), []);

    const handleShowNotes = useCallback(() => setIsNotesFieldVisible(true), []);
    const handleHideNotes = useCallback(() => {
      if (isMobile && trim(localNotes) === '') {
        setIsNotesFieldVisible(false);
      }
    }, [localNotes, isMobile]);

    useEffect(() => {
      setIsNotesFieldVisible(!isMobile || !!notes);
    }, [isMobile, notes]);

    useEffect(() => {
      if (notes) {
        setLocalNotes(notes);
      }
    }, [notes]);

    return (
      <Box
        className={classNames('confirmStepButton-ie', { [classes.mobileView]: isMobile })}
        width={1}
        mt={8}
        px={[3, 0]}
        py={2}
        display="flex"
        flexDirection={['column', 'row']}
        alignItems="center"
        justifyContent="center"
        position="relative"
      >
        {isClearButtonVisible && (
          <Box width={150} height={40} position="absolute" top={-40} textAlign="center">
            <Chip
              className={classes.deleteButton}
              classes={{ root: classes.deleteButton, deleteIcon: classes.chipDeleteIcon }}
              label={`Clear (${selectedSlotsCount}) selected`}
              size="small"
              onDelete={onClear}
            />
          </Box>
        )}
        {isNotesFieldVisible && (
          <Box width={['100%', 320]} mr={[0, 2]} mb={[2, 0]}>
            <BaseField
              name="notes"
              label="Any notes on the time(s) selected?"
              placeholder="Any notes on the time(s) selected?"
              fullWidth
              value={localNotes}
              errors={localErrors}
              disabled={isLoading}
              onChange={handleChangeNotes}
              inputProps={{ 'data-testid': 'MeetingNotesForm.Notes' }}
              onBlur={handleHideNotes}
              onFocus={handleResetError}
            />
          </Box>
        )}
        <Box ref={ref} width={[1, 'auto']} display="flex" alignItems="center" justifyContent="space-between">
          {!isNotesFieldVisible && (
            <LinkButton className={classes.noteLink} onClick={handleShowNotes}>
              Add a note
            </LinkButton>
          )}
          <BaseButton
            data-testid="MeetingNotesForm.Submit.Button"
            overrideClass={classes.submitButton}
            width="100%"
            bgColor={customStyling.primaryColor}
            color={customStyling.primaryCtaColor}
            hoverBgColor={darken(customStyling.primaryColor, 0.2)}
            hoverColor={lighten(customStyling.primaryCtaColor, 0.2)}
            border="none"
            disabled={disabled || isLoading}
            onClick={handleSubmit}
          >
            <Box mx={2}>{buttonText}</Box>
          </BaseButton>
        </Box>
      </Box>
    );
  },
);

export default memo(MeetingNotesForm);
